import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORIES, getSubcats } from "@/lib/categories";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Shield, Star, Pencil, Search, Sparkles } from "lucide-react";

const SectionLabel = ({ children }) => (
  <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#9CA47E] mb-3">{children}</div>
);

/* Reusable, OBVIOUSLY-interactive button class.
 * Always shows pointer cursor, gives clear opacity feedback when disabled,
 * and a hover state inverting the colour. */
const adminBtn =
  "px-2.5 py-1.5 border text-[10px] font-mono uppercase tracking-[0.2em] " +
  "cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed " +
  "inline-flex items-center justify-center gap-1";
const btnDark    = `${adminBtn} border-[#333333] text-[#333333] hover:bg-[#333333] hover:text-white`;
const btnRed     = `${adminBtn} border-[#9CA47E] text-[#9CA47E] hover:bg-[#9CA47E] hover:text-white`;
const btnRedFill = `${adminBtn} border-[#9CA47E] bg-[#9CA47E] text-white hover:bg-[#7E876A]`;
const btnGhost   = `${adminBtn} border-[#E5E5E5] text-[#595959] hover:border-[#333333] hover:text-[#333333]`;

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-[#EFEFEF] p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">{label}</div>
      <div className="font-heading text-4xl mt-2">{value}</div>
      {sub && <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959] mt-1">{sub}</div>}
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sets, setSets] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [productQ, setProductQ] = useState("");
  const [productCat, setProductCat] = useState("all");
  const [productSub, setProductSub] = useState("all");
  const [editing, setEditing] = useState(null); // product being edited
  const [categories, setCategories] = useState([]);
  const [editingCat, setEditingCat] = useState(null);
  const [newCatTop, setNewCatTop] = useState("");
  const [newCatForm, setNewCatForm] = useState({ value: "", label: "", thumbnail_url: "" });
  const [settings, setSettings] = useState(null);

  const loadStats = async () => (await api.get("/admin/stats")).data;
  const loadUsers = async () => (await api.get("/admin/users?limit=300")).data;
  const loadSets  = async () => (await api.get("/admin/sets?limit=300")).data;
  const loadCategories = async () => (await api.get("/admin/categories")).data;
  const loadProducts = async () => {
    const params = new URLSearchParams();
    if (productQ) params.set("q", productQ);
    if (productCat !== "all") params.set("category", productCat);
    if (productSub !== "all") params.set("subcategory", productSub);
    params.set("limit", "300");
    return (await api.get(`/admin/products?${params}`)).data;
  };

  const refreshAll = async () => {
    try {
      const [s, u, st, p, c] = await Promise.all([loadStats(), loadUsers(), loadSets(), loadProducts(), loadCategories()]);
      setStats(s); setUsers(u); setSets(st); setProducts(p); setCategories(c);
      setSelectedIds(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (!loading && user?.role === "admin") refreshAll();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [loading, user]);

  // Re-fetch products when filters change (only after we have an admin)
  useEffect(() => {
    if (!loading && user?.role === "admin") loadProducts().then(setProducts).catch(() => {});
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [productQ, productCat, productSub]);

  // Lazy-fetch site settings the first time the admin visits the settings tab.
  useEffect(() => {
    if (tab !== "settings") return;
    if (settings) return;
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, [tab, settings]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center font-mono text-xs uppercase tracking-widest">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-white border border-[#EFEFEF] p-10">
          <Shield className="w-8 h-8 text-[#9CA47E] mx-auto" />
          <h2 className="font-heading text-3xl mt-4">Admin only</h2>
          <p className="font-body text-sm text-[#595959] mt-3">
            You're signed in as <strong>@{user.username}</strong> but this page is for admins only.
          </p>
        </div>
      </div>
    );
  }

  // ---- User actions ----
  const setRole = async (id, role) => {
    setBusy(true);
    try { await api.post(`/admin/users/${id}/role?role=${role}`); toast.success(`Role: ${role}`); setUsers(await loadUsers()); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const deleteUser = async (id, username) => {
    if (!window.confirm(`Delete @${username} and all their sets? Cannot be undone.`)) return;
    setBusy(true);
    try { await api.delete(`/admin/users/${id}`); toast.success("User deleted"); setUsers(await loadUsers()); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds).filter(id => id !== user.id);
    if (ids.length === 0) { toast.error("Select at least one user"); return; }
    if (!window.confirm(`Delete ${ids.length} user${ids.length === 1 ? "" : "s"} and all their content?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/bulk-delete`, { user_ids: ids });
      toast.success(`Deleted ${data.deleted}`);
      setUsers(await loadUsers());
      setSelectedIds(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllVisible = () => setSelectedIds(new Set(users.filter(u => u.id !== user.id).map(u => u.id)));
  const clearSelection = () => setSelectedIds(new Set());

  // ---- Set actions ----
  const deleteSet = async (id, title) => {
    if (!window.confirm(`Delete the set "${title}"?`)) return;
    setBusy(true);
    try { await api.delete(`/admin/sets/${id}`); toast.success("Set deleted"); setSets(await loadSets()); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const toggleFeature = async (id, current) => {
    setBusy(true);
    try { await api.post(`/admin/sets/${id}/feature?featured=${!current}`); toast.success(!current ? "Featured" : "Unfeatured"); setSets(await loadSets()); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // ---- Product actions ----
  const deleteProduct = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This removes it from every set's library.`)) return;
    setBusy(true);
    try { await api.delete(`/admin/products/${id}`); toast.success("Product deleted"); setProducts(p => p.filter(x => x.id !== id)); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const saveProductEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/products/${editing.id}`, {
        title: editing.title,
        brand: editing.brand,
        price: editing.price,
        category: editing.category,
        subcategory: editing.subcategory,
      });
      toast.success("Product updated");
      setProducts(p => p.map(x => x.id === data.id ? data : x));
      setEditing(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // ---- Category actions ----
  const addCategory = async () => {
    if (!newCatForm.value || !newCatForm.label) { toast.error("Value + label required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/categories", {
        value: newCatForm.value, label: newCatForm.label,
        thumbnail_url: newCatForm.thumbnail_url || "",
        level: newCatTop ? "sub" : "top",
        parent: newCatTop || null,
      });
      toast.success("Category added");
      setCategories(await loadCategories());
      setNewCatForm({ value: "", label: "", thumbnail_url: "" });
      setNewCatTop("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const aiSuggestCategory = async () => {
    if (!newCatForm.label) { toast.error("Type a label first (e.g. 'Workout Wear')"); return; }
    setBusy(true);
    const t = toast.loading("Generating slug + thumbnail with AI…");
    try {
      const { data } = await api.post("/admin/categories/suggest", { label: newCatForm.label });
      setNewCatForm(f => ({ ...f, value: data.value || f.value, thumbnail_url: data.thumbnail_url || f.thumbnail_url }));
      toast.success(data.thumbnail_url ? "AI suggestion ready" : `Slug: ${data.value} (thumbnail unavailable)`, { id: t });
    } catch (e) { toast.error(formatApiError(e), { id: t }); }
    finally { setBusy(false); }
  };
  const saveCategoryEdit = async () => {
    if (!editingCat) return;
    setBusy(true);
    try {
      await api.patch(`/admin/categories/${editingCat.id}`, {
        label: editingCat.label, thumbnail_url: editingCat.thumbnail_url,
      });
      toast.success("Category updated");
      setCategories(await loadCategories());
      setEditingCat(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const deleteCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.label}"? ${c.product_count > 0 ? `It has ${c.product_count} products attached.` : ""}`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Category deleted");
      setCategories(await loadCategories());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Upload a thumbnail file (file input → /api/images/upload → fills thumbnail_url).
  // `target` is "new" (the add-form) or "edit" (the edit dialog).
  const uploadCatThumb = async (file, target) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setBusy(true);
    const t = toast.loading("Uploading thumbnail…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "new") setNewCatForm(f => ({ ...f, thumbnail_url: data.image_url }));
      else if (target === "edit" && editingCat) setEditingCat(c => ({ ...c, thumbnail_url: data.image_url }));
      toast.success("Thumbnail uploaded", { id: t });
    } catch (e) { toast.error(formatApiError(e) || "Upload failed", { id: t }); }
    finally { setBusy(false); }
  };

  // ---------- Site Settings (admin-editable footer & brand chrome) ----------
  const updateSettingField = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const updateLinkField = (key, idx, field, v) => setSettings(s => ({
    ...s,
    [key]: s[key].map((l, i) => i === idx ? { ...l, [field]: v } : l),
  }));
  const addLink = (key) => setSettings(s => ({ ...s, [key]: [...(s[key] || []), { label: "", to: "" }] }));
  const removeLink = (key, idx) => setSettings(s => ({ ...s, [key]: s[key].filter((_, i) => i !== idx) }));
  const saveSettings = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Settings saved — footer will update on next page load");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const tabBtn = (key, label) => (
    <button
      key={key}
      type="button"
      data-testid={`admin-tab-${key}`}
      onClick={() => setTab(key)}
      className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] border cursor-pointer transition-colors ${
        tab === key ? "bg-[#333333] text-white border-[#333333]" : "bg-white border-[#E5E5E5] hover:border-[#333333]"
      }`}
    >
      {label}
    </button>
  );

  const roleBadgeColor = (r) => r === "admin" ? "text-[#9CA47E]" : r === "curator" ? "text-[#333333]" : "text-[#595959]";

  return (
    <div className="bg-[#F2EDE3] min-h-screen">
      <section className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="border-b border-[#E5E5E5] pb-6 mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <SectionLabel>◇ Admin Console</SectionLabel>
            <h1 className="font-heading text-5xl tracking-tight">VoraStyle Studio</h1>
            <p className="font-body text-[#595959] mt-2">Signed in as <strong>@{user.username}</strong> · role: <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA47E]">{user.role}</span></p>
          </div>
          <div className="flex flex-wrap gap-2">
            {tabBtn("stats",      "Stats")}
            {tabBtn("users",      `Users (${users.length})`)}
            {tabBtn("products",   `Gathered Pieces (${products.length})`)}
            {tabBtn("categories", `Archive (${categories.length})`)}
            {tabBtn("sets",       `Sets (${sets.length})`)}
            {tabBtn("settings",   "Site Settings")}
          </div>
        </div>

        {/* ---- Stats ---- */}
        {tab === "stats" && stats && (
          <div data-testid="admin-stats" className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <StatCard label="Users" value={stats.users} sub={`${stats.admins} admin`} />
              <StatCard label="Sets" value={stats.sets} sub={`${stats.featured_sets} featured`} />
              <StatCard label="Products" value={stats.products} />
              <StatCard label="Likes" value={stats.engagement.likes} sub={`${stats.engagement.comments} comments`} />
              <StatCard label="Donations" value={`$${stats.donations.total}`} sub={`${stats.donations.count} tips`} />
            </div>
          </div>
        )}

        {/* ---- Users ---- */}
        {tab === "users" && (
          <div data-testid="admin-users" className="bg-white border border-[#EFEFEF]">
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#EFEFEF] bg-[#F2EDE3] flex-wrap">
              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">
                <button type="button" data-testid="admin-users-select-all" onClick={selectAllVisible} className="cursor-pointer hover:text-[#333333]">Select all</button>
                <span>·</span>
                <button type="button" data-testid="admin-users-clear" onClick={clearSelection} className="cursor-pointer hover:text-[#333333]">Clear</button>
                <span className="ml-2 text-[#333333]">{selectedIds.size} selected</span>
              </div>
              <button type="button" data-testid="admin-users-bulk-delete" disabled={busy || selectedIds.size === 0} onClick={bulkDelete} className={btnRed}>
                <Trash2 className="w-3 h-3" /> Delete selected
              </button>
            </div>

            <div className="grid grid-cols-[40px_1fr_1.2fr_110px_90px_80px_240px] gap-3 px-5 py-3 border-b border-[#EFEFEF] font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">
              <div></div><div>Username</div><div>Email</div><div>Role</div><div>Plan</div><div>Sets</div><div>Actions</div>
            </div>
            {users.map((u) => {
              const isSelf = u.id === user.id;
              return (
                <div key={u.id} className="grid grid-cols-[40px_1fr_1.2fr_110px_90px_80px_240px] gap-3 px-5 py-3 border-b border-[#EFEFEF] items-center text-sm">
                  <div>
                    <input type="checkbox" data-testid={`admin-user-checkbox-${u.id}`} disabled={isSelf} checked={selectedIds.has(u.id)} onChange={() => toggleSelect(u.id)} className="w-4 h-4 accent-[#9CA47E] cursor-pointer disabled:cursor-not-allowed disabled:opacity-30" />
                  </div>
                  <div className="font-body truncate">@{u.username}{isSelf && <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#9CA47E]">(you)</span>}</div>
                  <div className="font-mono text-[11px] truncate">{u.email}</div>
                  <div className={`font-mono text-[10px] uppercase tracking-[0.2em] ${roleBadgeColor(u.role)}`}>{u.role || "user"}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">{u.plan}</div>
                  <div className="font-body">{u.sets_count}</div>
                  <div className="flex gap-1.5 flex-wrap">
                    {u.role !== "curator" && (
                      <button type="button" data-testid={`make-curator-${u.id}`} disabled={busy} onClick={() => setRole(u.id, "curator")} className={btnDark}>Curator</button>
                    )}
                    {u.role !== "admin" && (
                      <button type="button" data-testid={`make-admin-${u.id}`} disabled={busy} onClick={() => setRole(u.id, "admin")} className={btnRed}>Admin</button>
                    )}
                    {(u.role === "admin" || u.role === "curator") && (
                      <button type="button" data-testid={`demote-${u.id}`} disabled={busy || isSelf} onClick={() => setRole(u.id, "user")} className={btnGhost}>Demote</button>
                    )}
                    <button type="button" data-testid={`del-user-${u.id}`} disabled={busy || isSelf} onClick={() => deleteUser(u.id, u.username)} className={btnRed} aria-label="Delete user">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
            {users.length === 0 && <div className="px-5 py-8 text-center font-body text-[#595959]">No users.</div>}
          </div>
        )}

        {/* ---- Products ---- */}
        {tab === "products" && (
          <div data-testid="admin-products" className="space-y-4">
            <div className="bg-white border border-[#EFEFEF] p-4 flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#595959]" />
                <Input
                  data-testid="admin-products-search"
                  placeholder="Search products by title or brand"
                  value={productQ}
                  onChange={(e) => setProductQ(e.target.value)}
                  className="rounded-none border-[#E5E5E5] focus-visible:ring-0 focus-visible:border-[#333333] pl-10"
                />
              </div>
              <select
                data-testid="admin-products-category"
                value={productCat}
                onChange={(e) => { setProductCat(e.target.value); setProductSub("all"); }}
                className="border border-[#E5E5E5] bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-widest cursor-pointer hover:border-[#333333]"
              >
                <option value="all">All archive</option>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {productCat !== "all" && (
                <select
                  data-testid="admin-products-subcategory"
                  value={productSub}
                  onChange={(e) => setProductSub(e.target.value)}
                  className="border border-[#E5E5E5] bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-widest cursor-pointer hover:border-[#333333]"
                >
                  <option value="all">All chapters</option>
                  {getSubcats(productCat).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {products.map((p) => (
                <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-white border border-[#EFEFEF]">
                  <div className="aspect-square bg-[#F2EDE3] overflow-hidden">
                    <img src={resolveImageUrl(p.image_url)} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-2.5">
                    <div className="text-[12px] font-body truncate">{p.title}</div>
                    <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#595959] truncate mt-0.5">
                      {p.category}{p.subcategory ? ` · ${p.subcategory}` : ""}
                    </div>
                    <div className="flex gap-1 mt-2">
                      <button type="button" data-testid={`edit-product-${p.id}`} disabled={busy} onClick={() => setEditing({ ...p, brand: p.brand || "", price: p.price || "", subcategory: p.subcategory || "" })} className={`flex-1 ${btnDark}`}>
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      <button type="button" data-testid={`del-product-${p.id}`} disabled={busy} onClick={() => deleteProduct(p.id, p.title)} className={btnRed} aria-label="Delete product">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {products.length === 0 && <div className="col-span-full py-12 text-center font-body text-[#595959]">No gathered pieces match.</div>}
            </div>
          </div>
        )}

        {/* ---- Categories ---- */}
        {tab === "categories" && (
          <div data-testid="admin-categories" className="space-y-6">
            {/* Add new */}
            <div className="bg-white border border-[#EFEFEF] p-5">
              <SectionLabel>+ Add a chapter</SectionLabel>
              <p className="font-body text-xs text-[#595959] -mt-1 mb-3">
                Tip: type a label like <em>“Workout Wear”</em>, then hit <span className="font-mono uppercase tracking-widest text-[#9CA47E]">AI Suggest</span> — it generates the slug and a Gemini Nano Banana thumbnail in one click.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_1fr_1fr_140px_140px] gap-3 items-end">
                <label className="block">
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Type</span>
                  <select
                    data-testid="admin-newcat-parent"
                    value={newCatTop}
                    onChange={(e) => setNewCatTop(e.target.value)}
                    className="w-full mt-1 border border-[#E5E5E5] bg-white px-2 py-2 font-mono text-[11px] uppercase tracking-widest cursor-pointer hover:border-[#333333]"
                  >
                    <option value="">Top-level</option>
                    {categories.filter(c => c.level === "top").map(c =>
                      <option key={c.value} value={c.value}>Sub of: {c.label}</option>
                    )}
                  </select>
                </label>
                <label className="block">
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Value (slug)</span>
                  <Input data-testid="admin-newcat-value" placeholder="e.g. swimwear"
                    value={newCatForm.value}
                    onChange={(e) => setNewCatForm(f => ({ ...f, value: e.target.value }))}
                    className="rounded-none border-[#E5E5E5] mt-1" />
                </label>
                <label className="block">
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Label</span>
                  <Input data-testid="admin-newcat-label" placeholder="e.g. Swimwear"
                    value={newCatForm.label}
                    onChange={(e) => setNewCatForm(f => ({ ...f, label: e.target.value }))}
                    className="rounded-none border-[#E5E5E5] mt-1" />
                </label>
                <label className="block">
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Thumbnail</span>
                  <div className="flex gap-1 mt-1">
                    <Input data-testid="admin-newcat-thumb" placeholder="https://… or use upload"
                      value={newCatForm.thumbnail_url}
                      onChange={(e) => setNewCatForm(f => ({ ...f, thumbnail_url: e.target.value }))}
                      className="rounded-none border-[#E5E5E5] flex-1" />
                    <label data-testid="admin-newcat-thumb-upload" className={`${btnDark} cursor-pointer shrink-0`} title="Upload an image">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCatThumb(e.target.files?.[0], "new")} />
                      ↑
                    </label>
                  </div>
                </label>
                <button type="button" data-testid="admin-newcat-aisuggest" disabled={busy || !newCatForm.label} onClick={aiSuggestCategory} className={`${btnDark} h-10 mt-5`}>
                  <Sparkles className="w-3 h-3" /> AI Suggest
                </button>
                <button type="button" data-testid="admin-newcat-add" disabled={busy} onClick={addCategory} className={`${btnRedFill} h-10 mt-5`}>
                  Add
                </button>
              </div>
              {newCatForm.thumbnail_url && (
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-16 h-16 bg-[#F2EDE3] border border-[#E5E5E5] overflow-hidden">
                    <img src={resolveImageUrl(newCatForm.thumbnail_url)} alt="preview" className="w-full h-full object-cover" />
                  </div>
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Thumbnail preview</span>
                </div>
              )}
            </div>

            {/* Top-level groups */}
            {categories.filter(c => c.level === "top").map(top => {
              const subs = categories.filter(c => c.level === "sub" && c.parent === top.value);
              return (
                <div key={top.id} className="bg-white border border-[#EFEFEF]">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-[#EFEFEF] bg-[#F2EDE3]">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#EFEFEF] overflow-hidden">
                        {top.thumbnail_url && <img src={resolveImageUrl(top.thumbnail_url)} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div>
                        <div className="font-heading text-2xl">{top.label} <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959] ml-2">/{top.value}/</span></div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">{subs.length} chapters · {top.product_count} pieces{top.custom ? " · custom" : ""}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" data-testid={`edit-cat-${top.id}`} disabled={busy} onClick={() => setEditingCat({ ...top })} className={btnDark}>
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      <button type="button" data-testid={`del-cat-${top.id}`} disabled={busy || top.product_count > 0 || subs.length > 0} onClick={() => deleteCategory(top)} className={btnRed} title={top.product_count > 0 ? "Has pieces" : subs.length > 0 ? "Has chapters" : ""}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  {subs.length === 0 ? (
                    <div className="px-5 py-6 font-body text-sm text-[#595959]">No chapters yet.</div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 p-3">
                      {subs.map(s => (
                        <div key={s.id} className="border border-[#EFEFEF] bg-white" data-testid={`admin-cat-${s.value}`}>
                          <div className="aspect-square bg-[#F2EDE3] overflow-hidden">
                            {s.thumbnail_url ? (
                              <img src={resolveImageUrl(s.thumbnail_url)} alt={s.label} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">no image</div>
                            )}
                          </div>
                          <div className="p-2">
                            <div className="text-[12px] font-body truncate" title={s.label}>{s.label}</div>
                            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#595959] truncate" title={`internal slug: /${s.value}/`}>
                              {s.product_count} {s.product_count === 1 ? "piece" : "pieces"}{s.custom ? " · custom" : ""}
                            </div>
                            <div className="flex gap-1 mt-2">
                              <button type="button" data-testid={`edit-cat-${s.id}`} disabled={busy} onClick={() => setEditingCat({ ...s })} className={`flex-1 ${btnDark}`}>
                                <Pencil className="w-3 h-3" /> Edit
                              </button>
                              <button type="button" data-testid={`del-cat-${s.id}`} disabled={busy} onClick={() => deleteCategory(s)} className={btnRed} aria-label="Delete">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ---- Sets ---- */}
        {tab === "sets" && (
          <div data-testid="admin-sets" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sets.map((s) => (
              <div key={s.id} className="bg-white border border-[#EFEFEF]">
                <div className="aspect-[4/3] bg-[#F2EDE3] relative overflow-hidden">
                  {s.cover_image && <img src={resolveImageUrl(s.cover_image)} alt="" className="w-full h-full object-cover" />}
                  {s.featured && <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-[#9CA47E] text-white font-mono text-[9px] uppercase tracking-[0.2em]">Featured</div>}
                </div>
                <div className="p-3">
                  <div className="font-heading text-lg leading-tight truncate">{s.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959] mt-1 truncate">@{s.author?.username || "?"} · {s.category} · {s.items?.length || 0} items</div>
                  <div className="flex gap-1 mt-2">
                    <button type="button" data-testid={`feature-${s.id}`} disabled={busy} onClick={() => toggleFeature(s.id, s.featured)} className={`flex-1 ${btnDark}`}>
                      <Star className={`w-3 h-3 ${s.featured ? "fill-current" : ""}`} /> {s.featured ? "Unfeature" : "Feature"}
                    </button>
                    <button type="button" data-testid={`del-set-${s.id}`} disabled={busy} onClick={() => deleteSet(s.id, s.title)} className={btnRed}>
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {sets.length === 0 && <div className="col-span-full text-center font-body text-[#595959] py-10">No sets.</div>}
          </div>
        )}

        {/* ---- Site Settings ---- */}
        {tab === "settings" && (
          <div data-testid="admin-settings" className="space-y-6 max-w-4xl">
            {!settings ? (
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Loading settings…</div>
            ) : (
              <>
                <div className="bg-white border border-[#EFEFEF] p-5">
                  <SectionLabel>◇ Brand & footer copy</SectionLabel>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Brand name</span>
                      <Input data-testid="settings-brand-name" value={settings.brand_name || ""} onChange={(e) => updateSettingField("brand_name", e.target.value)} className="rounded-none border-[#E5E5E5] mt-1" />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Tagline</span>
                      <Input data-testid="settings-tagline" value={settings.tagline || ""} onChange={(e) => updateSettingField("tagline", e.target.value)} className="rounded-none border-[#E5E5E5] mt-1" />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Support email</span>
                      <Input data-testid="settings-support-email" value={settings.support_email || ""} onChange={(e) => updateSettingField("support_email", e.target.value)} className="rounded-none border-[#E5E5E5] mt-1" />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Version label</span>
                      <Input data-testid="settings-version" value={settings.version_label || ""} onChange={(e) => updateSettingField("version_label", e.target.value)} className="rounded-none border-[#E5E5E5] mt-1" />
                    </label>
                    <label className="block md:col-span-2">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Copyright text</span>
                      <Input data-testid="settings-copyright" value={settings.copyright_text || ""} onChange={(e) => updateSettingField("copyright_text", e.target.value)} className="rounded-none border-[#E5E5E5] mt-1" />
                    </label>
                  </div>
                </div>

                <div className="bg-white border border-[#EFEFEF] p-5">
                  <SectionLabel>◇ Social URLs</SectionLabel>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="block">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Facebook</span>
                      <Input data-testid="settings-fb" value={settings.facebook_url || ""} onChange={(e) => updateSettingField("facebook_url", e.target.value)} className="rounded-none border-[#E5E5E5] mt-1" />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Instagram</span>
                      <Input data-testid="settings-ig" value={settings.instagram_url || ""} onChange={(e) => updateSettingField("instagram_url", e.target.value)} className="rounded-none border-[#E5E5E5] mt-1" />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Pinterest</span>
                      <Input data-testid="settings-pi" value={settings.pinterest_url || ""} onChange={(e) => updateSettingField("pinterest_url", e.target.value)} className="rounded-none border-[#E5E5E5] mt-1" />
                    </label>
                  </div>
                </div>

                {[
                  { key: "footer_links_explore", title: "Footer · Explore column" },
                  { key: "footer_links_company", title: "Footer · Company column" },
                ].map(({ key, title }) => (
                  <div key={key} className="bg-white border border-[#EFEFEF] p-5">
                    <div className="flex items-center justify-between mb-3">
                      <SectionLabel>◇ {title}</SectionLabel>
                      <button type="button" data-testid={`add-${key}`} disabled={busy} onClick={() => addLink(key)} className={btnDark}>+ Add link</button>
                    </div>
                    <div className="space-y-2">
                      {(settings[key] || []).map((l, idx) => (
                        <div key={idx} className="grid grid-cols-[1fr_2fr_60px] gap-2 items-center" data-testid={`${key}-row-${idx}`}>
                          <Input value={l.label || ""} placeholder="Label" data-testid={`${key}-label-${idx}`} onChange={(e) => updateLinkField(key, idx, "label", e.target.value)} className="rounded-none border-[#E5E5E5]" />
                          <Input value={l.to || ""} placeholder="/path" data-testid={`${key}-to-${idx}`} onChange={(e) => updateLinkField(key, idx, "to", e.target.value)} className="rounded-none border-[#E5E5E5]" />
                          <button type="button" data-testid={`${key}-del-${idx}`} disabled={busy} onClick={() => removeLink(key, idx)} className={btnRed}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {(settings[key] || []).length === 0 && (
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">No links yet.</div>
                      )}
                    </div>
                  </div>
                ))}

                <div className="flex justify-end gap-2">
                  <button type="button" data-testid="settings-save" disabled={busy} onClick={saveSettings} className={`${btnRedFill} px-5 py-2.5`}>
                    {busy ? "Saving…" : "Save site settings"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </section>

      {/* ---- Edit Product Dialog ---- */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="rounded-none border-[#E5E5E5]" data-testid="edit-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit product</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="aspect-video bg-[#F2EDE3] overflow-hidden">
                <img src={resolveImageUrl(editing.image_url)} alt={editing.title} className="w-full h-full object-contain" />
              </div>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Title</span>
                <Input data-testid="edit-product-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="rounded-none border-[#E5E5E5] mt-1" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Brand</span>
                  <Input data-testid="edit-product-brand" value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} className="rounded-none border-[#E5E5E5] mt-1" />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Price</span>
                  <Input data-testid="edit-product-price" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="rounded-none border-[#E5E5E5] mt-1" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Category</span>
                  <select
                    data-testid="edit-product-category"
                    value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value, subcategory: "" })}
                    className="w-full mt-1 border border-[#E5E5E5] bg-white px-2 py-2 font-mono text-[11px] uppercase tracking-widest cursor-pointer"
                  >
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Sub-category</span>
                  <select
                    data-testid="edit-product-subcategory"
                    value={editing.subcategory}
                    onChange={(e) => setEditing({ ...editing, subcategory: e.target.value })}
                    className="w-full mt-1 border border-[#E5E5E5] bg-white px-2 py-2 font-mono text-[11px] uppercase tracking-widest cursor-pointer"
                  >
                    <option value="">— None —</option>
                    {getSubcats(editing.category).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </label>
              </div>
            </div>
          )}
          <DialogFooter className="mt-4 gap-2">
            <button type="button" data-testid="edit-product-cancel" onClick={() => setEditing(null)} className={btnGhost}>Cancel</button>
            <button type="button" data-testid="edit-product-save" disabled={busy} onClick={saveProductEdit} className={btnRedFill}>
              {busy ? "Saving…" : "Save changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Edit Category Dialog ---- */}
      <Dialog open={!!editingCat} onOpenChange={(open) => !open && setEditingCat(null)}>
        <DialogContent className="rounded-none border-[#E5E5E5]" data-testid="edit-cat-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit category</DialogTitle>
          </DialogHeader>
          {editingCat && (
            <div className="space-y-3">
              {editingCat.thumbnail_url && (
                <div className="aspect-video bg-[#F2EDE3] overflow-hidden">
                  <img src={resolveImageUrl(editingCat.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Label</span>
                <Input data-testid="edit-cat-label" value={editingCat.label} onChange={(e) => setEditingCat({ ...editingCat, label: e.target.value })} className="rounded-none border-[#E5E5E5] mt-1" />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Thumbnail</span>
                <div className="flex gap-1 mt-1">
                  <Input data-testid="edit-cat-thumb" value={editingCat.thumbnail_url || ""} onChange={(e) => setEditingCat({ ...editingCat, thumbnail_url: e.target.value })} className="rounded-none border-[#E5E5E5] flex-1" placeholder="https://… or use upload" />
                  <label data-testid="edit-cat-thumb-upload" className={`${btnDark} cursor-pointer shrink-0`} title="Upload an image">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCatThumb(e.target.files?.[0], "edit")} />
                    ↑ Upload
                  </label>
                </div>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959] mt-1">Leave blank to auto-pick a recent product image.</p>
              </label>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">Slug: <code className="text-[#333333]">/{editingCat.value}/</code> · Level: {editingCat.level}{editingCat.parent ? ` · in ${editingCat.parent}` : ""}</p>
            </div>
          )}
          <DialogFooter className="mt-4 gap-2">
            <button type="button" data-testid="edit-cat-cancel" onClick={() => setEditingCat(null)} className={btnGhost}>Cancel</button>
            <button type="button" data-testid="edit-cat-save" disabled={busy} onClick={saveCategoryEdit} className={btnRedFill}>
              {busy ? "Saving…" : "Save changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
